const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_ONLY = /^(\d{4})-(\d{2})$/;
const UK_TIME_ZONE = "Europe/London";

function validDate(value: unknown): Date | null {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value !== "string" || !value.trim()) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/** Formats a customer-facing date as DD/MM/YYYY without shifting ISO date-only values across time zones. */
export function formatUkDate(value: unknown, fallback = "—") {
    if (typeof value === "string") {
        const match = DATE_ONLY.exec(value.trim());
        if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    }
    const date = validDate(value);
    return date
        ? new Intl.DateTimeFormat("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              timeZone: UK_TIME_ZONE,
          }).format(date)
        : fallback;
}

export function formatUkTime(value: unknown, fallback = "—") {
    const date = validDate(value);
    return date
        ? new Intl.DateTimeFormat("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              hourCycle: "h23",
              timeZone: UK_TIME_ZONE,
          }).format(date)
        : fallback;
}

export function formatUkDateTime(value: unknown, fallback = "—") {
    const date = validDate(value);
    return date ? `${formatUkDate(date)} ${formatUkTime(date)}` : fallback;
}

/** Returns the calendar date in the UK venue timezone for filtering and reconciliation reports. */
export function ukIsoDate(value: unknown, fallback = "") {
    if (typeof value === "string" && DATE_ONLY.test(value.trim())) return value.trim();
    const date = validDate(value);
    if (!date) return fallback;
    const parts = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: UK_TIME_ZONE,
    }).formatToParts(date);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(value => value.type === type)?.value ?? "";
    return `${part("year")}-${part("month")}-${part("day")}`;
}

export function formatUkDateLong(value: unknown, fallback = "—") {
    const date = validDate(value);
    return date
        ? new Intl.DateTimeFormat("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
              timeZone: UK_TIME_ZONE,
          }).format(date)
        : fallback;
}

/** Formats an ISO year-month value as a customer-facing UK month, for example "August 2026". */
export function formatUkMonth(value: unknown, fallback = "—") {
    if (typeof value === "string") {
        const match = MONTH_ONLY.exec(value.trim());
        if (match) {
            const month = Number(match[2]);
            if (month >= 1 && month <= 12) {
                return new Intl.DateTimeFormat("en-GB", {
                    month: "long",
                    year: "numeric",
                    timeZone: "UTC",
                }).format(new Date(Date.UTC(Number(match[1]), month - 1, 1)));
            }
        }
    }
    const date = validDate(value);
    return date
        ? new Intl.DateTimeFormat("en-GB", {
              month: "long",
              year: "numeric",
          }).format(date)
        : fallback;
}

export function formatUkDateRange(start: Date, end: Date) {
    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = sameYear && start.getMonth() === end.getMonth();
    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = start.toLocaleDateString("en-GB", {
        month: sameMonth ? "long" : "short",
    });
    const endMonth = end.toLocaleDateString("en-GB", {
        month: sameMonth ? "long" : "short",
    });

    if (sameMonth) return `${startDay}–${endDay} ${endMonth} ${end.getFullYear()}`;
    if (sameYear)
        return `${startDay} ${startMonth} – ${endDay} ${endMonth} ${end.getFullYear()}`;
    return `${startDay} ${startMonth} ${start.getFullYear()} – ${endDay} ${endMonth} ${end.getFullYear()}`;
}
